"use client";

import Image from "next/image";
import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import CourseCard from "@/components/CourseCard/CourseCard";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { getGoalLabelKey } from "@/lib/recommendations";

/**
 * Marqueur d'étape du parcours : l'icône de la formation (terminée = pastille
 * de validation, en cours = anneau, à venir = estompée), ou un fanion pour
 * l'objectif final.
 */
function JourneyMarker({ node }) {
    // Objectif atteint : trophée plein (état « pathCompleted »)
    if (node.type === "reached") {
        return (
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-white">
                <SolarIcon name="cup-star-bold" className="w-5 h-5" />
            </span>
        );
    }
    if (node.type === "goal") {
        return (
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <SolarIcon name="flag-2-linear" className="w-5 h-5" />
            </span>
        );
    }

    const ring =
        node.type === "current"
            ? "ring-2 ring-primary ring-offset-2 ring-offset-white dark:ring-offset-bgDark-840"
            : "ring-1 ring-gray-200 dark:ring-grayDark-700";

    return (
        <span
            className={`relative flex h-11 w-11 items-center justify-center rounded-xl bg-white dark:bg-white/10 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:shadow-md ${ring} ${
                node.type === "upcoming"
                    ? "opacity-50 group-hover:opacity-100"
                    : ""
            }`}
        >
            {node.image ? (
                <Image
                    src={node.image}
                    alt=""
                    width={44}
                    height={44}
                    className="rounded-xl"
                />
            ) : (
                <SolarIcon
                    name="notebook-linear"
                    className="w-5 h-5 text-gray-400"
                />
            )}
            {node.type === "done" && (
                <span className="absolute -bottom-1 -right-1 rounded-full bg-white ring-2 ring-white dark:bg-white/10 dark:ring-bgDark-840">
                    <SolarIcon
                        name="check-circle-bold"
                        className="block h-4 w-4 text-primary"
                    />
                </span>
            )}
        </span>
    );
}

/** Titre + statut d'une étape, avec effet de survol si c'est un lien. */
function StepLabel({ node, clickable }) {
    return (
        <>
            <p
                className={`mt-2 text-xs leading-tight ${
                    node.type === "upcoming"
                        ? "text-gray-400 dark:text-grayDark-400"
                        : "font-medium text-gray-900 dark:text-white"
                } ${clickable ? "group-hover:text-primary transition-colors" : ""}`}
            >
                {node.title}
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400 dark:text-grayDark-400">
                {node.statusLabel}
            </p>
        </>
    );
}

/** Icône d'étape en format ligne (40px, sans pastille — l'état est porté par
 * la ligne : coche à droite, surlignage pour l'étape courante / atteinte). */
function RowMarker({ node }) {
    if (node.type === "reached") {
        return (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
                <SolarIcon name="cup-star-bold" className="h-5 w-5" />
            </span>
        );
    }
    if (node.type === "goal") {
        return (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <SolarIcon name="flag-2-linear" className="h-5 w-5" />
            </span>
        );
    }
    const ring =
        node.type === "current"
            ? "ring-2 ring-primary"
            : "ring-1 ring-gray-200 dark:ring-grayDark-700";
    return (
        <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white dark:bg-white/10 ${ring} ${
                node.type === "upcoming" ? "opacity-50" : ""
            }`}
        >
            {node.image ? (
                <Image
                    src={node.image}
                    alt=""
                    width={40}
                    height={40}
                    className="rounded-xl"
                />
            ) : (
                <SolarIcon
                    name="notebook-linear"
                    className="h-5 w-5 text-gray-400"
                />
            )}
        </span>
    );
}

/**
 * Feuille de route en lignes (mobile, et colonne étroite du cas « cours neuf ») :
 * icône + titre + statut, coche à droite pour les étapes terminées, ligne
 * surlignée pour l'étape courante et pour l'objectif atteint.
 * `nodes` : [{ key, slug, image, title, type, statusLabel }].
 */
function RoadmapStepsVertical({ nodes }) {
    return (
        <ul className="flex flex-1 flex-col">
            {nodes.map((node) => {
                const clickable =
                    node.type !== "goal" && node.type !== "reached";
                const highlight =
                    node.type === "current" || node.type === "reached";
                const inner = (
                    <>
                        <RowMarker node={node} />
                        <div className="min-w-0 flex-1">
                            <p
                                className={`text-sm mb-0 ${
                                    node.type === "upcoming"
                                        ? "text-gray-400 dark:text-grayDark-400"
                                        : "font-medium text-gray-900 dark:text-white"
                                } ${clickable ? "group-hover:text-primary transition-colors" : ""}`}
                            >
                                {node.title}
                            </p>
                            <p
                                className={`text-xs mb-0 ${
                                    node.type === "reached"
                                        ? "text-primary font-medium"
                                        : "text-gray-400 dark:text-grayDark-400"
                                }`}
                            >
                                {node.statusLabel}
                            </p>
                        </div>
                        {node.type === "done" && (
                            <SolarIcon
                                name="check-circle-bold"
                                className="h-5 w-5 shrink-0 text-primary"
                            />
                        )}
                    </>
                );
                // Icônes alignées sur le bord du contenu (colonne nette) : les
                // lignes n'ont pas de padding horizontal ; la ligne surlignée
                // déborde son fond dans le padding via -mx-3 sans bouger l'icône.
                const rowClass = highlight
                    ? "flex items-center gap-3 -mx-3 rounded-xl bg-primary/[0.06] px-3 py-3"
                    : "flex items-center gap-3 border-b border-gray-100 py-3 last:border-b-0 dark:border-borderDark";
                return (
                    <li key={node.key}>
                        {clickable ? (
                            <Link
                                href={`/course/${node.slug}`}
                                className={`group ${rowClass}`}
                            >
                                {inner}
                            </Link>
                        ) : (
                            <div className={rowClass}>{inner}</div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Feuille de route horizontale (desktop) : les étapes s'étalent sur toute la
 * largeur, connectées par un trait. Chaque formation est cliquable.
 */
function RoadmapStepsHorizontal({ nodes, completed }) {
    const lineClass = completed
        ? "bg-primary/40"
        : "bg-gray-200 dark:bg-grayDark-700";
    return (
        <ol className="flex items-start">
            {nodes.map((node, index) => {
                const isFirst = index === 0;
                const isLast = index === nodes.length - 1;
                const clickable =
                    node.type !== "goal" && node.type !== "reached";
                const content = (
                    <>
                        <span className="relative z-10">
                            <JourneyMarker node={node} />
                        </span>
                        <StepLabel node={node} clickable={clickable} />
                    </>
                );
                return (
                    <li
                        key={node.key}
                        className="relative flex flex-1 flex-col items-center px-1 text-center"
                    >
                        {/* Traits de liaison : ils s'arrêtent AVANT et
                            reprennent APRÈS l'icône (jamais derrière), donc
                            l'opacité de l'icône n'a aucun effet dessus. */}
                        {!isFirst && (
                            <span
                                aria-hidden
                                className={`absolute left-0 right-[calc(50%_+_28px)] top-[22px] h-px ${lineClass}`}
                            />
                        )}
                        {!isLast && (
                            <span
                                aria-hidden
                                className={`absolute left-[calc(50%_+_28px)] right-0 top-[22px] h-px ${lineClass}`}
                            />
                        )}
                        {clickable ? (
                            <Link
                                href={`/course/${node.slug}`}
                                className="group flex flex-col items-center"
                            >
                                {content}
                            </Link>
                        ) : (
                            <div className="flex flex-col items-center">
                                {content}
                            </div>
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

/**
 * Recommandations du dashboard (voix plateforme, vouvoiement) : une seule
 * « prochaine étape » issue du parcours de l'objectif + deux compléments, et
 * la redéfinition d'objectif en un clic. La carte de la prochaine étape et
 * les compléments réutilisent le composant partagé CourseCard : elles sont
 * visuellement identiques aux cartes de /courses. Données : lib/recommendations.js.
 */
export default function RecommendedPath({ recommendation }) {
    const t = useTranslations("dashboard");
    // Libellés d'objectifs : clés au niveau racine (onboarding_goal_*)
    const tRoot = useTranslations();

    const { needsGoal, pathCompleted, goal, nextStep, complements, journey } =
        recommendation;
    const posthog = usePostHog();

    const isEmpty =
        !needsGoal && !pathCompleted && !nextStep && complements.length === 0;

    // Mesure : impression de la recommandation (pour évaluer/optimiser).
    useEffect(() => {
        if (isEmpty || !posthog) return;
        posthog.capture("recommendation_displayed", {
            goal: goal || null,
            pathCompleted,
            needsGoal,
            nextStepSlug: nextStep?.course?.slug || null,
            complementsCount: complements.length,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mesure : un seul gestionnaire capte tous les clics de la section
    // (étapes du parcours, compléments, CTA, redéfinition d'objectif).
    const handleClickCapture = (event) => {
        if (!posthog) return;
        const link = event.target.closest("a");
        if (!link) return;
        const href = link.getAttribute("href") || "";
        if (href.includes("/course/")) {
            const slug = href.split("/course/")[1]?.split(/[/?#]/)[0];
            posthog.capture("recommendation_clicked", {
                courseSlug: slug || null,
                goal: goal || null,
            });
        } else if (href.includes("/onboarding")) {
            posthog.capture("recommendation_goal_redefined", {
                goal: goal || null,
            });
        }
    };

    // Rien d'actionnable : ne pas afficher de section vide
    if (isEmpty) {
        return null;
    }

    const goalLabelKey = getGoalLabelKey(goal);

    const headerTitle = needsGoal
        ? t("reco_needs_goal_title")
        : pathCompleted
          ? t("reco_completed_title")
          : t("reco_title");

    const headerSubtitle = needsGoal
        ? t("reco_needs_goal_subtitle")
        : pathCompleted
          ? t("reco_completed_subtitle")
          : goalLabelKey
            ? t("reco_subtitle_goal", { goal: tRoot(goalLabelKey) })
            : t("reco_subtitle_generic");

    // Nœuds de la feuille de route : étapes du parcours + objectif final.
    // Partagé par « prochaine étape » et « objectif atteint » (journey top-level).
    const journeyNodes =
        journey && journey.length
            ? [
                  ...journey.map((step) => ({
                      key: step.slug,
                      slug: step.slug,
                      image: step.image,
                      title: step.title,
                      type: step.status,
                      statusLabel:
                          step.status === "done"
                              ? t("reco_journey_done")
                              : step.status === "current"
                                ? step.progress > 0
                                    ? t("reco_journey_here_progress", {
                                          progress: step.progress,
                                      })
                                    : t("reco_journey_here")
                                : t("reco_journey_upcoming"),
                  })),
                  goalLabelKey && {
                      key: "__goal__",
                      type: pathCompleted ? "reached" : "goal",
                      title: tRoot(goalLabelKey),
                      statusLabel: pathCompleted
                          ? t("reco_journey_reached")
                          : t("reco_journey_goal"),
                  },
              ].filter(Boolean)
            : [];

    // Style de carte commun (aligné sur les cartes de /courses)
    const cardClass =
        "rounded-xl border border-gray-200 dark:border-borderDark bg-white dark:bg-bgDark-840";
    // CTA principal : bouton 3D (lèvre orange) du design system ; blanc forcé
    // au survol (évite la couleur de lien globale)
    const ctaClass =
        "bg-primary hover:bg-primary/90 text-white hover:text-white btn-3d lip-orange";

    return (
        <div className="mt-14 lg:mt-24" onClickCapture={handleClickCapture}>
            {/* Header (voix plateforme, sans mascotte) */}
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {headerTitle}
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                        {headerSubtitle}
                    </p>
                </div>
                {!needsGoal && !pathCompleted && (
                    <Link
                        href={{
                            pathname: "/onboarding",
                            query: { redefine: "1" },
                        }}
                        className="text-primary text-sm font-medium flex items-center gap-1.5 flex-shrink-0 mt-1"
                    >
                        <SolarIcon name="target-linear" className="w-4 h-4" />
                        <span className="sm:inline hidden">
                            {t("reco_redefine_goal")}
                        </span>
                    </Link>
                )}
            </div>

            {/* Cold start : invitation à définir un objectif */}
            {needsGoal ? (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-borderDark bg-gray-50 dark:bg-bgDark-840 p-6">
                    <div className="flex flex-col md:flex-row md:items-center gap-5 text-center md:text-left">
                        <p className="text-sm text-gray-600 dark:text-grayDark-300 flex-1 mb-0">
                            {t("reco_needs_goal_message")}
                        </p>
                        <Button asChild className={`${ctaClass} flex-shrink-0`}>
                            <Link
                                href={{
                                    pathname: "/onboarding",
                                    query: { redefine: "1" },
                                }}
                            >
                                {t("reco_needs_goal_cta")}
                            </Link>
                        </Button>
                    </div>
                </div>
            ) : pathCompleted ? (
                // Objectif atteint : tout le parcours validé + objectif en
                // trophée, puis relance vers un nouvel objectif.
                <div className={`${cardClass} p-6`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-grayDark-400 mb-5">
                        {t("reco_journey_title")}
                    </p>
                    <div className="hidden lg:block">
                        <RoadmapStepsHorizontal nodes={journeyNodes} completed />
                    </div>
                    <div className="lg:hidden">
                        <RoadmapStepsVertical nodes={journeyNodes} />
                    </div>
                    <div className="mt-6 flex flex-col items-center gap-3 border-t border-gray-100 dark:border-grayDark-700 pt-5 text-center">
                        <p className="text-sm text-gray-600 dark:text-grayDark-300 mb-0">
                            {t("reco_completed_prompt")}
                        </p>
                        <Button asChild className={ctaClass}>
                            <Link
                                href={{
                                    pathname: "/onboarding",
                                    query: { redefine: "1" },
                                }}
                            >
                                {t("reco_completed_cta")}
                            </Link>
                        </Button>
                    </div>
                </div>
            ) : nextStep && nextStep.state === "resume" ? (
                // Cours déjà commencé (présent dans « Vos formations » au-dessus) :
                // pas de carte répétée. Feuille de route PLEINE LARGEUR —
                // horizontale sur desktop, verticale sur mobile.
                <div className={`${cardClass} p-6`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-grayDark-400 mb-5">
                        {t("reco_journey_title")}
                    </p>
                    <div className="hidden lg:block">
                        <RoadmapStepsHorizontal nodes={journeyNodes} />
                    </div>
                    <div className="lg:hidden">
                        <RoadmapStepsVertical nodes={journeyNodes} />
                    </div>
                </div>
            ) : nextStep ? (
                // Cours neuf (pas encore commencé) : carte du cours + feuille de
                // route verticale, côte à côte (remplit la largeur, pas de vide).
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
                    <CourseCard course={nextStep.course} />

                    <div className={`${cardClass} flex flex-col p-6`}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-grayDark-400 mb-4">
                            {t("reco_journey_title")}
                        </p>
                        <RoadmapStepsVertical nodes={journeyNodes} />
                        <Button
                            asChild
                            className={`${ctaClass} w-full sm:w-fit mt-5`}
                        >
                            <Link href={`/course/${nextStep.course.slug}`}>
                                {t("reco_start")}
                            </Link>
                        </Button>
                    </div>
                </div>
            ) : null}

            {/* Compléments : mêmes cartes que /courses, avec leur raison */}
            {complements.length > 0 && (
                <>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-grayDark-300 mt-8 mb-3">
                        {needsGoal
                            ? t("reco_complements_waiting")
                            : t("reco_complements_title")}
                    </h3>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        {complements.map(({ course, reasonKey }) => (
                            // Sous-grille : la carte et sa raison partagent les
                            // rangées de la grille, les cartes d'une même ligne
                            // ont donc la même hauteur, que la raison tienne
                            // sur une ligne ou deux.
                            <div
                                key={course.slug}
                                className="grid grid-rows-subgrid row-span-2 gap-y-2"
                            >
                                <CourseCard course={course} />
                                <p className="text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                    {t(reasonKey)}
                                </p>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
